import { type ChangeEvent, forwardRef, memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';
import { useGlobalSearchContext } from '~/modules/global-search/hooks/useGlobalSearchContext';
import { useKeyboardNavigationContext } from '~/modules/global-search/hooks/useKeyboardNavigationContext';

interface SearchFieldProps {
  className?: string;
  placeholder?: string;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
export const SearchField: React.FC<SearchFieldProps> = memo(
  forwardRef<HTMLFieldSetElement, SearchFieldProps>(
    ({ className, placeholder, ...props }, ref) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const { t } = useTranslation('wmgone');
      const { searchQuery, setSearchQuery, resetSearch } =
        useGlobalSearchContext();
      const { registerElements } = useKeyboardNavigationContext();

      const handleSearchQueryInputChange = (
        event: ChangeEvent<HTMLInputElement>,
      ) => {
        const { value } = event.target;

        setSearchQuery(value);
      };

      const handleClearSearchButtonClick = () => {
        resetSearch();
        inputRef.current?.focus();
      };

      useEffect(() => {
        if (!inputRef.current) {
          return;
        }

        registerElements({ type: 'input', payload: inputRef.current });
      }, [registerElements]);

      return (
        <div
          className={cn(
            'group relative overflow-hidden rounded-xs p-px transition-colors duration-300 bg-emphasis-subtle hover:bg-emphasis-strong',
            'before:absolute before:inset-0 before:z-0 before:rounded-xs before:opacity-none before:duration-300',
            'before:pointer-events-none before:focus-within:block before:focus-within:opacity-full',
            'before:bg-[linear-gradient(90deg,oklch(var(--bg-primary-default))62.87%,oklch(var(--bg-tertiary-default))105.06%)]',
          )}
        >
          <fieldset
            {...props}
            className={cn(
              'relative z-10 flex w-full items-center rounded-xs bg-default',
              className,
            )}
            ref={ref}
          >
            <div className="pointer-events-none absolute px-sm pl-md text-subtle group-focus-within:text-default">
              <Icon
                type="search"
                variant="light"
                size="sm"
                aria-label="search-icon"
                aria-hidden
              />
            </div>
            <input
              ref={inputRef}
              onChange={handleSearchQueryInputChange}
              value={searchQuery}
              className={cn(
                'w-full rounded-xs py-sm pl-2xl text-body-sm italic outline-none outline-0 duration-300 bg-emphasis-default placeholder:text-subtle',
                'hover:outline-none hover:outline-0 focus:outline-none focus:outline-0 focus:bg-emphasis-subtle focus:placeholder:text-default group-hover:bg-emphasis-subtle',
              )}
              placeholder={placeholder || t('globalSearch.inputPlaceholder')}
            />
            {searchQuery ? (
              <div className="absolute right-sm duration-300 text-subtle focus-within:text-icon-strong hover:text-icon-strong">
                <IconButton
                  type="button"
                  iconType="circle-xmark"
                  size="small"
                  onClick={handleClearSearchButtonClick}
                  aria-label={t('globalSearch.clearInput')}
                />
              </div>
            ) : null}
          </fieldset>
        </div>
      );
    },
  ),
);

SearchField.displayName = 'SearchField';
